/**
 * blindly-tier-reconciler.js
 *
 * Keeps every SUBSCRIPTION book line on the Loop selling plan that matches the
 * cart's current book count, so the "more books = bigger discount" tiering can't
 * be gamed (e.g. add 3 to get the deep tier, then delete two and keep one cheap)
 * and so existing books get upgraded when another is added.
 *
 * One-time (OTP) lines are left alone — the Athena discount function recomputes
 * their discount from the cart at checkout, so they self-correct.
 *
 * Config comes from window.__blindlyTiers (set by snippets/blindly-tier-config),
 * which only renders when the feature is enabled. Reassignment uses the AJAX
 * Cart API: POST /cart/change.js with { line, quantity, selling_plan } — the one
 * supported way to change a line's selling plan in place.
 */
(function () {
  var CFG = window.__blindlyTiers;
  if (!CFG || !CFG.plans) return;

  var MAX = CFG.max || 4;
  var running = false;

  // The set of tier plan IDs. We only ever reassign lines that are ALREADY on
  // one of these (i.e. added by the upsell / bundle builder). Organic standalone
  // genre subs live in a DIFFERENT Loop plan group, so we must never touch them.
  var TIER_PLANS = {};
  Object.keys(CFG.plans).forEach(function (k) {
    var id = String(CFG.plans[k] || '').trim();
    if (id) TIER_PLANS[id] = true;
  });

  // A "book" = any non-gift cart line (matches the upsell widget's counting).
  function isBook(item) {
    var props = item.properties || {};
    return props._bundle_role !== 'gift';
  }

  function planFor(tier) {
    return String(CFG.plans[String(tier)] || '').trim();
  }

  function reconcile() {
    if (running) return Promise.resolve();
    running = true;

    return fetch('/cart.js', { headers: { Accept: 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        var items = (cart && cart.items) || [];

        var bookCount = 0;
        items.forEach(function (it) { if (isBook(it)) bookCount += it.quantity; });
        if (bookCount < 1) return false;

        var tier = Math.min(bookCount, MAX);
        var target = planFor(tier);
        if (!target) return false;

        // Sub book lines on the wrong plan. change.js identifies the line by its
        // 1-based index, and these edits don't add/remove lines, so indices stay
        // stable across the batch.
        var changes = [];
        items.forEach(function (it, idx) {
          if (!isBook(it)) return;
          var alloc = it.selling_plan_allocation;
          if (!alloc || !alloc.selling_plan) return; // OTP — skip (function handles it)
          var current = String(alloc.selling_plan.id);
          // Only manage lines already on a tier plan; leave organic standalone
          // subs (different plan group) untouched.
          if (!TIER_PLANS[current]) return;
          if (current !== target) {
            changes.push({ line: idx + 1, quantity: it.quantity });
          }
        });
        if (!changes.length) return false;
        try { if (typeof window.blindlyTrack === 'function') window.blindlyTrack('cart_tier_reassigned', { lines: changes.map(function (c) { return c.line; }), target_plan: target, tier: tier, book_count: bookCount }); } catch (e) { /* noop */ }

        // Apply sequentially; a single failure (e.g. plan not valid for that
        // product) shouldn't abort the rest.
        return changes.reduce(function (chain, ch) {
          return chain.then(function () {
            return fetch('/cart/change.js', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
              body: JSON.stringify({ line: ch.line, quantity: ch.quantity, selling_plan: target })
            }).catch(function (e) { console.warn('[bcu-tier] line', ch.line, 'reassign failed', e); });
          });
        }, Promise.resolve()).then(function () { return true; });
      })
      .then(function (changed) {
        running = false;
        if (changed) {
          // Refresh the visible cart. On /cart it's server-rendered (reload);
          // elsewhere the mini-cart drawer listens for cart:refresh.
          if (window.location.pathname === '/cart') {
            window.location.reload();
          } else {
            document.dispatchEvent(new CustomEvent('cart:refresh'));
          }
        }
        return changed;
      })
      .catch(function (e) {
        running = false;
        try { if (typeof window.blindlyTrack === 'function') window.blindlyTrack('cart_reconcile_failed', { which: 'tier', message: String(e && e.message || e).slice(0, 200) }); } catch (err) { /* noop */ }
        console.warn('[bcu-tier] reconcile failed', e);
      });
  }

  // Run on load + on every cart mutation. Uses the theme's pubsub via BARE
  // globals (subscribe / PUB_SUB_EVENTS) — same as bundle-gift-reconciler.
  // window.subscribe is NOT reliably defined (pubsub.js exposes lexical globals).
  function init() {
    reconcile();
    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      subscribe(PUB_SUB_EVENTS.cartUpdate, function (data) {
        if (data && data.source === 'blindly-tier-reconciler') return;
        reconcile();
      });
    }
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
